/**
 * Chord names, and the three things a player does to them.
 *
 * Ported from the engine's player so the web app, the print sheet and the
 * eventual phone app all render a chord identically — a chart that says Bbm in
 * one place and A#m in another has already lost the reader's trust.
 */

const NAMES = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];
const PITCH: Record<string, number> = {
  C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5,
  "F#": 6, Gb: 6, G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11,
};
const CHORD = /^([A-G][#b]?)(.*)$/;

export const isNoChord = (label: string) => !label || label === "N";

/** Move a chord by semitones, keeping its quality and any slash bass. */
export function shift(label: string, semitones: number): string {
  if (isNoChord(label) || !semitones) return label;
  const [head, bass] = label.split("/");
  const match = CHORD.exec(head);
  if (!match || !(match[1] in PITCH)) return label;

  let root = NAMES[(((PITCH[match[1]] + semitones) % 12) + 12) % 12];
  // G#m reads better than Abm; the reverse holds for the major.
  const minor = match[2].startsWith("m") && !match[2].startsWith("maj");
  if (root === "Ab" && minor) root = "G#";

  const moved = root + match[2];
  if (!bass) return moved;
  const bassMatch = CHORD.exec(bass);
  if (!bassMatch || !(bassMatch[1] in PITCH)) return moved;
  return `${moved}/${NAMES[(((PITCH[bassMatch[1]] + semitones) % 12) + 12) % 12]}`;
}

/** Reduce to a triad. Detection reports sevenths at about 61% accuracy against
 *  ground truth versus 81% for triads, so this is a real choice, not a
 *  simplification for beginners. */
export function simplify(label: string): string {
  if (isNoChord(label)) return label;
  const match = CHORD.exec(label.split("/")[0]);
  if (!match) return label;
  const minor = /^(m(?!aj)|dim)/.test(match[2]);
  return match[1] + (minor ? "m" : "");
}

export interface ChordView {
  transpose: number;   // what the reader asked for
  capo: number;        // shapes move down by this; written pitch does not
  simple: boolean;
}

/** What to print for a chord, given how the reader has set the page up. */
export function display(label: string, view: ChordView): string {
  const moved = shift(label, view.transpose - view.capo);
  return view.simple ? simplify(moved) : moved;
}
